import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  BilledType,
  BillingBreakdownKey,
  BillingBreakdownTile,
  BillingHistoryResponse,
  BillingMatterData,
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_RANGE_OPTION,
  DateRangeOption,
  computeDateRange,
  formatBillingHours,
  getBilledTypeLabel,
} from '../../../models/billing-history.models';
import { Breadcrumb } from '../../../services/breadcrumb.service';
import { ResourceService } from '../../../services/resource.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { SnackbarService } from '../../../services/snackbar.service';
import {
  PageKey,
  UserCookieService,
} from '../../../services/user-cookie.service';
import { BillingTotalTileComponent } from '../billing-total-tile/billing-total-tile.component';
import { MatterCellComponent } from '../matter-cell/matter-cell.component';
import { TaskBreakdownComponent } from '../task-breakdown/task-breakdown.component';
import { TaskBreakdownExpansion } from '../task-breakdown/task-breakdown-expansion';
import { describeApiError } from '../../../utils/api-error.utils';

@Component({
  selector: 'app-resource-billing-history',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule,
    MatDatepickerModule,
    MatNativeDateModule,
    RouterModule,
    MatterCellComponent,
    TaskBreakdownComponent,
    BillingTotalTileComponent,
  ],
  templateUrl: './resource-billing-history.component.html',
  styleUrls: ['./resource-billing-history.component.scss'],
})
export class ResourceBillingHistoryComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private resourceService = inject(ResourceService);
  private snackbarService = inject(SnackbarService);
  private featureFlagService = inject(FeatureFlagService);
  private cdr = inject(ChangeDetectorRef);
  private userCookieService = inject(UserCookieService);
  private router = inject(Router);

  readonly employeeId = input.required<string>();
  readonly breadcrumbContext = input<Breadcrumb[]>([]);
  readonly paginator = viewChild.required(MatPaginator);
  readonly sort = viewChild.required(MatSort);

  isLoading = false;
  error: string | null = null;
  billingData: BillingMatterData[] = [];
  dataSource!: MatTableDataSource<BillingMatterData>;
  selectedDateRange: DateRangeOption = DEFAULT_DATE_RANGE_OPTION; // Will be loaded from settings
  dateRangeOptions = DATE_RANGE_OPTIONS;
  private readonly SETTINGS_PAGE_KEY = PageKey.ResourceDetails;
  pageSize = 5; // Default page size
  isDropdownOpen = false;
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;
  showStartDatePicker = false;
  showEndDatePicker = false;
  /** The 'expand' column is added only while the task-breakdowns flag is on — see ngOnInit. */
  displayedColumns: string[] = ['clientMatter', 'billedType', 'totalHours'];
  /**
   * Gates the task-breakdown expander on the `task-breakdowns` flag. Starts false and is only
   * raised once the flag is confirmed enabled, so a slow or failed lookup hides the expander
   * rather than offering one that opens onto nothing.
   */
  taskBreakdownsEnabled = false;

  /** Which rows are open, plus the count label beside each chevron. */
  readonly expansion = new TaskBreakdownExpansion<BillingMatterData>();

  /**
   * Whether a row can be expanded at all. A skeleton row never can; a real row can even when
   * the feed supplied no task data, because "no task detail available" is itself worth
   * surfacing rather than leaving the user to wonder why some rows expand and others do not.
   */
  canExpand(row: BillingMatterData): boolean {
    return this.taskBreakdownsEnabled && !this.isSkeletonRow(row);
  }


  // Totals from the response
  totalBilledHours: number = 0;
  totalUnbilledHours: number = 0;
  totalProBonoHours: number = 0;
  totalBusinessDevelopmentHours: number = 0;
  totalAbsenceHours: number = 0;
  totalFirmHours: number = 0;
  grandTotalHours: number = 0;

  // Per-type breakdown derived from the totals. Recomputed only when a load
  // completes (see updateBreakdown), not on every change-detection cycle.
  billingBreakdown: BillingBreakdownTile[] = [];
  billingBarSegments: BillingBreakdownTile[] = [];
  grandTotalDisplay = '';

  // Key of the distribution-bar segment currently hovered, or null. Drives the
  // movable emphasis outline: null => Total tile is outlined; a type key =>
  // that type's tile is outlined instead.
  hoveredBarType: BillingBreakdownKey | null = null;

  private destroy$ = new Subject<void>();
  private loadingSubscription?: Subscription;
  private requestCounter = 0;
  private clickListener?: () => void;

  navigateToMatter(clientMatterNumber: string, event?: Event): void {
    event?.preventDefault();
    if (!clientMatterNumber) return;
    const breadcrumbContext = this.breadcrumbContext();
    const state = breadcrumbContext.length
      ? {
          breadcrumbOverride: [
            ...breadcrumbContext,
            { label: ':matterId', url: '' },
          ],
        }
      : undefined;
    this.router.navigate(['/matters', clientMatterNumber], { state });
  }

  ngOnInit(): void {
    this.featureFlagService
      .isFeatureFlagEnabled('task-breakdowns')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.taskBreakdownsEnabled = enabled;

        // Add/remove the column rather than just hiding its contents. The table is
        // `table-layout: fixed` with the name column at `width: auto`, which is not a
        // specified width — so an empty expand column would still claim half the remaining
        // space and halve the name column, flag off or not.
        const hasColumn = this.displayedColumns.includes('expand');
        if (enabled && !hasColumn) {
          this.displayedColumns = [...this.displayedColumns, 'expand'];
        } else if (!enabled && hasColumn) {
          this.displayedColumns = this.displayedColumns.filter((c) => c !== 'expand');
        }

        // A disabled flag must also close anything already open, not just hide the toggle.
        if (!enabled) {
          this.expansion.clear();
        }
      });

    this.dataSource = new MatTableDataSource<BillingMatterData>();
    if (!this.employeeId()) {
      this.error = 'Employee ID is required';
      return;
    }
    // Load saved timespan preference from settings
    this.selectedDateRange = this.userCookieService.getBillingHistoryTimespan(
      this.SETTINGS_PAGE_KEY,
    );

    // Load saved custom dates if custom range is selected
    if (this.isCustomRange()) {
      const savedDates = this.userCookieService.getCustomDateRange(
        this.SETTINGS_PAGE_KEY,
      );
      if (savedDates) {
        this.customStartDate = savedDates.startDate;
        this.customEndDate = savedDates.endDate;
      }
    }

    // Initialize custom dates with the loaded range
    this.initializeCustomDates();
    this.loadBillingHistory();
  }

  ngAfterViewInit(): void {
    // Connect paginator and sort after view initialization
    this.dataSource.paginator = this.paginator();
    this.dataSource.sort = this.sort();

    // Configure custom sort accessor for clientMatter column to sort by matterName
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'clientMatter') {
        return item.matterName?.toLowerCase() || '';
      }
      return (item as any)[property];
    };
  }

  ngOnDestroy(): void {
    this.cancelPendingRequest();
    this.removeClickListener();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPageChange(event: any): void {
    // Store the user's page size preference
    this.pageSize = event.pageSize;

    // If currently loading, update skeleton data to match new page size
    if (this.isLoading) {
      this.showSkeletonData();
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      this.setupClickListener();
    } else {
      this.removeClickListener();
    }
  }

  selectDateRange(option: DateRangeOption): void {
    this.selectedDateRange = option;
    this.isDropdownOpen = false;
    this.removeClickListener();
    this.error = null;

    // Save the selected timespan to settings
    this.userCookieService.setBillingHistoryTimespan(
      this.SETTINGS_PAGE_KEY,
      option,
    );

    // Update custom dates to match the selected range
    this.initializeCustomDates();

    this.loadBillingHistory();
  }

  isCustomRange(): boolean {
    return this.selectedDateRange.kind === 'custom';
  }

  /**
   * Initialize custom dates based on the selected date range option
   */
  private initializeCustomDates(): void {
    const today = new Date();

    if (this.isCustomRange()) {
      // For custom range, only set defaults if not already loaded from cookie
      if (!this.customStartDate) {
        this.customStartDate = new Date(today);
        this.customStartDate.setDate(this.customStartDate.getDate() - 30);
      }
      if (!this.customEndDate) {
        this.customEndDate = new Date(today);
      }
    } else {
      // For preset ranges, seed the custom dates from the computed window so the
      // displayed start/end text stays in sync with the selection.
      const { startDate, endDate } = computeDateRange(this.selectedDateRange);
      this.customStartDate = this.parseLocalDate(startDate);
      this.customEndDate = this.parseLocalDate(endDate);
    }
  }

  /** Parse a local `yyyy-MM-dd` string into a Date at local midnight. */
  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  onStartDateChange(event: any): void {
    const date = event?.value || event;
    if (date) {
      this.customStartDate = date;
      this.showStartDatePicker = false;

      // Automatically switch to Custom range if not already selected
      if (!this.isCustomRange()) {
        this.selectedDateRange =
          this.dateRangeOptions.find((opt) => opt.kind === 'custom') ||
          this.selectedDateRange;
        // Save the custom range selection
        this.userCookieService.setBillingHistoryTimespan(
          this.SETTINGS_PAGE_KEY,
          this.selectedDateRange,
        );
      }

      // Save custom dates to cookie
      if (this.customStartDate && this.customEndDate) {
        this.userCookieService.setCustomDateRange(
          this.SETTINGS_PAGE_KEY,
          this.customStartDate,
          this.customEndDate,
        );
      }

      this.loadBillingHistory();
    }
  }

  onEndDateChange(event: any): void {
    const date = event?.value || event;
    if (date) {
      this.customEndDate = date;
      this.showEndDatePicker = false;

      // Automatically switch to Custom range if not already selected
      if (!this.isCustomRange()) {
        this.selectedDateRange =
          this.dateRangeOptions.find((opt) => opt.kind === 'custom') ||
          this.selectedDateRange;
        // Save the custom range selection
        this.userCookieService.setBillingHistoryTimespan(
          this.SETTINGS_PAGE_KEY,
          this.selectedDateRange,
        );
      }

      // Save custom dates to cookie
      if (this.customStartDate && this.customEndDate) {
        this.userCookieService.setCustomDateRange(
          this.SETTINGS_PAGE_KEY,
          this.customStartDate,
          this.customEndDate,
        );
      }

      this.loadBillingHistory();
    }
  }

  private setupClickListener(): void {
    // Close dropdown when clicking outside
    this.clickListener = () => {
      this.isDropdownOpen = false;
      this.removeClickListener();
      this.cdr.detectChanges();
    };

    // Add small delay to prevent immediate closing
    setTimeout(() => {
      document.addEventListener('click', this.clickListener!);
    }, 0);
  }

  private removeClickListener(): void {
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
      this.clickListener = undefined;
    }
  }

  private cancelPendingRequest(): void {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
      this.loadingSubscription = undefined;
    }
  }

  private loadBillingHistory(): void {
    const employeeId = this.employeeId();
    if (!employeeId) {
      this.error = 'Employee ID is required';
      return;
    }

    // Cancel any pending request
    this.cancelPendingRequest();

    this.isLoading = true;
    this.error = null;
    // Drop expansions here rather than in each result branch, so the error and empty paths
    // release their rows too. The Set holds row objects, and this fetch is about to replace
    // every one of them — without this it would keep the old ones alive for good.
    this.expansion.clear();
    // Clear any pending hover emphasis so it can't stick to a stale tile when
    // the bar is replaced by the skeleton (its mouseleave never fires).
    this.hoveredBarType = null;

    // Show skeleton data while loading. Seed the breakdown tile shells once on
    // the first load so their skeleton placeholders render; later loads keep the
    // existing shells (their values are hidden behind the skeleton anyway), so
    // there's no need to re-derive them from the previous load's stale totals.
    this.showSkeletonData();
    if (!this.billingBreakdown.length) {
      this.updateBreakdown();
    }

    // Increment request counter to track the latest request
    const currentRequestId = ++this.requestCounter;

    const { startDate, endDate } = this.calculateDateRange();

    this.loadingSubscription = this.resourceService
      // billedTypes omitted => all types
      .getBillingHistory(employeeId, startDate, endDate)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          // Only update loading state if this is the latest request
          if (currentRequestId === this.requestCounter) {
            this.isLoading = false;
          }
        }),
      )
      .subscribe({
        next: (response: BillingHistoryResponse) => {
          // Ignore stale responses
          if (currentRequestId !== this.requestCounter) {
            return;
          }

          if (response) {
            const matterData = response.matters || [];

            this.applyTotals(response);
            this.updateBreakdown();

            // Flatten entries into individual rows
            this.billingData = [];
            matterData.forEach((matter) => {
              if (matter.entries && matter.entries.length > 0) {
                matter.entries.forEach((entry) => {
                  this.billingData.push({
                    matterName: matter.matterName,
                    clientName: matter.clientName,
                    clientMatter: matter.clientMatter,
                    clientMatterNumber: matter.clientMatterNumber,
                    // This row IS one entry, so it carries that entry's own breakdown — the
                    // matter-level rollup would describe every bill type at once and overstate
                    // the row's own hours.
                    taskSummary: entry.taskSummary,
                    totalHours: entry.totalHours,
                    billedType: entry.billedType,
                  });
                });
              }
            });
            // Set data - the paginator will automatically detect the length
            // Stamp panel ids before the rows are rendered, so the template's
            // aria-controls binding is a lookup rather than an assignment.
            this.expansion.prepare(this.billingData);
            this.dataSource.data = this.billingData;

            // Force change detection to show table
            this.cdr.detectChanges();

            // Use setTimeout to ensure paginator and sort connect after view updates
            setTimeout(() => {
              // Ensure paginator and sort are connected
              const paginator = this.paginator();
              this.dataSource.paginator = paginator;
              paginator.firstPage();
              this.dataSource.sort = this.sort();
            }, 0);
          } else {
            // Clear data when no results
            this.billingData = [];
            this.dataSource.data = [];
            this.resetTotals();
            this.updateBreakdown();

            // Ensure paginator is reset for empty data
            this.paginator().firstPage();
          }
        },
        error: (error) => {
          // Ignore errors from stale requests
          if (currentRequestId !== this.requestCounter) {
            return;
          }

          this.error = describeApiError(error, 'Failed to load billing history');
          this.billingData = [];
          this.dataSource.data = [];
          this.resetTotals();
          this.updateBreakdown();

          // Reset paginator on error
          this.paginator().firstPage();

          this.snackbarService.showError('Failed to load billing history');
        },
      });
  }

  private calculateDateRange(): { startDate: string; endDate: string } {
    return computeDateRange(this.selectedDateRange, {
      startDate: this.customStartDate,
      endDate: this.customEndDate,
    });
  }

  retry(): void {
    this.loadBillingHistory();
  }

  /**
   * Recompute the per-type breakdown tiles, the non-zero distribution-bar
   * segments and the formatted grand total from the current response totals.
   * Called only when the totals change (load start/success/empty/error) so the
   * template binds plain fields instead of re-deriving on every CD cycle. This
   * surfaces the by-type split that previously lived behind the "Total" modal.
   */
  private updateBreakdown(): void {
    const total = this.grandTotalHours;
    // Labels come from the shared map so they stay in sync with the table;
    // Business Development is abbreviated to fit the compact tile row.
    const types: { key: BillingBreakdownKey; label: string; hours: number }[] = [
      {
        key: 'billable',
        label: getBilledTypeLabel(BilledType.Billable),
        hours: this.totalBilledHours,
      },
      {
        key: 'non-billable',
        label: getBilledTypeLabel(BilledType.NonBillable),
        hours: this.totalUnbilledHours,
      },
      {
        key: 'pro-bono',
        label: getBilledTypeLabel(BilledType.ProBono),
        hours: this.totalProBonoHours,
      },
      {
        key: 'business-dev',
        label: 'Business Dev.',
        hours: this.totalBusinessDevelopmentHours,
      },
      {
        key: 'absence',
        label: getBilledTypeLabel(BilledType.Absence),
        hours: this.totalAbsenceHours,
      },
      {
        key: 'firm',
        label: getBilledTypeLabel(BilledType.Firm),
        hours: this.totalFirmHours,
      },
    ];

    this.billingBreakdown = types.map((t) => {
      // Derive isZero from the same one-decimal rounding used for display, so a
      // sub-0.05 value is treated as zero everywhere (dimmed, no bar segment, no
      // percent, en dash) rather than showing "– <1%" beside an invisible bar.
      const isZero = Number(t.hours.toFixed(1)) <= 0;
      const share = total > 0 ? (t.hours / total) * 100 : 0;
      return {
        key: t.key,
        label: t.label,
        isZero,
        valueDisplay: formatBillingHours(t.hours),
        percentDisplay: this.formatSharePercent(isZero, share),
        widthPercent: share,
      };
    });
    this.billingBarSegments = this.billingBreakdown.filter((t) => !t.isZero);
    this.grandTotalDisplay = formatBillingHours(this.grandTotalHours);
  }

  /** Copy the per-type and grand totals off a response onto the fields the breakdown reads. */
  private applyTotals(response: BillingHistoryResponse): void {
    this.totalBilledHours = response.totalBilledHours ?? 0;
    this.totalUnbilledHours = response.totalNonbilledHours ?? 0;
    this.totalProBonoHours = response.totalProbonoHours ?? 0;
    this.totalBusinessDevelopmentHours = response.totalBusinessDevelopmentHours ?? 0;
    this.totalAbsenceHours = response.totalAbsenceHours ?? 0;
    this.totalFirmHours = response.totalFirmHours ?? 0;
    this.grandTotalHours = response.totalHours ?? 0;
  }

  /** Zero every response total (used by the empty-result and error paths). */
  private resetTotals(): void {
    this.totalBilledHours = 0;
    this.totalUnbilledHours = 0;
    this.totalProBonoHours = 0;
    this.totalBusinessDevelopmentHours = 0;
    this.totalAbsenceHours = 0;
    this.totalFirmHours = 0;
    this.grandTotalHours = 0;
  }

  /**
   * Format a share as a whole percent. `isZero` (no hours) yields no percent;
   * any tile that has hours but a sub-1% share — including a degenerate 0 share
   * when the grand total is inconsistent with the parts — collapses to "<1%" so
   * a tile never shows real hours next to a misleading "0%".
   */
  private formatSharePercent(isZero: boolean, share: number): string {
    if (isZero) {
      return '';
    }
    if (share < 1) {
      return '<1%';
    }
    return `${Math.round(share)}%`;
  }

  /**
   * Get display label for billed type enum
   */
  getBilledTypeLabel(billedType: BilledType | null | undefined): string {
    return getBilledTypeLabel(billedType);
  }

  /**
   * Format hours for the table cells: an en dash when the value rounds to zero,
   * otherwise the value with trailing zeros stripped (e.g. "6", "12.5").
   */
  formatHours(value: number): string {
    return formatBillingHours(value, { stripTrailingZeros: true });
  }

  /**
   * Get formatted date range text based on selected option
   */
  getDateRangeText(): string {
    const { startDate, endDate } = this.calculateDateRange();
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(startDateObj)} — ${formatDate(endDateObj)}`;
  }

  /**
   * Get formatted date range text based on selected option
   */
  getDateRangeStartText(): string {
    const { startDate, endDate } = this.calculateDateRange();
    // Create date from the ISO string components to avoid timezone shift
    const [year, month, day] = startDate.split('-').map(Number);
    const startDateObj = new Date(year, month - 1, day);

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(startDateObj)}`;
  }

  /**
   * Get formatted date range text based on selected option
   */
  getDateRangeEndText(): string {
    const { startDate, endDate } = this.calculateDateRange();
    // Create date from the ISO string components to avoid timezone shift
    const [year, month, day] = endDate.split('-').map(Number);
    const endDateObj = new Date(year, month - 1, day);

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(endDateObj)}`;
  }

  /**
   * Format a single date for the date picker display
   */
  formatSingleDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Show skeleton data while loading
   */
  private showSkeletonData(): void {
    const skeletonData = this.createSkeletonData(this.pageSize);
    this.dataSource.data = skeletonData;
  }

  /**
   * Create skeleton data for the table
   */
  private createSkeletonData(count: number): BillingMatterData[] {
    return Array(count)
      .fill(null)
      .map((_, index) => ({
        clientMatter: `SKELETON-${index}`,
        clientMatterNumber: '',
        matterName: '',
        clientName: '',
        totalHours: 0,
        billedType: BilledType.Billable, // Rotate through billed types for variety
        totalBilledHours: 0,
        totalNonbilledHours: 0,
        totalProbonoHours: 0,
        totalBusinessDevelopmentHours: 0,
        totalAbsenceHours: 0,
        totalFirmHours: 0,
        // Add a flag to identify skeleton rows
        isSkeleton: true,
      }));
  }

  /**
   * Check if a row is a skeleton row
   */
  isSkeletonRow(element: any): boolean {
    return (
      element?.isSkeleton === true ||
      element?.clientMatter?.startsWith('SKELETON-')
    );
  }

  /**
   * Check if there is billing history data (or if we're loading, which shows skeleton)
   */
  hasBillingHistory(): boolean {
    return this.isLoading || (this.billingData && this.billingData.length > 0);
  }
}
